import { Record, List } from 'immutable';
import { join, toUpper, find, includes } from 'lodash';

import { LocaleService } from '@sm360/widget-commons-core-services';
import { PurchaseMethods } from '@sm360/widget-commons-vehicle';

import * as BODY_STYLES from 'catalog/constants/bodyStyles.js';
import * as FILTERS from 'catalog/constants/filter.js';
import BodyStyleFilterModel from 'catalog/models/BodyStyleFilterModel.js';
import * as CatalogFilterService from 'catalog/services/CatalogFilterService.js';

const { PaymentFrequencyService, PurchaseMethodsValues, PaymentFrequencies } = PurchaseMethods;

/* eslint no-bitwise: 0 */

const bodyStyles = List([
    new BodyStyleFilterModel({
        type: BODY_STYLES.SEDAN.TYPE,
        codes: BODY_STYLES.SEDAN.CODES,
        value: BODY_STYLES.SEDAN.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.COUPE.TYPE,
        codes: BODY_STYLES.COUPE.CODES,
        value: BODY_STYLES.COUPE.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.HATCHBACK.TYPE,
        codes: BODY_STYLES.HATCHBACK.CODES,
        value: BODY_STYLES.HATCHBACK.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.CONVERTIBLE.TYPE,
        codes: BODY_STYLES.CONVERTIBLE.CODES,
        value: BODY_STYLES.CONVERTIBLE.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.SUV.TYPE,
        codes: BODY_STYLES.SUV.CODES,
        value: BODY_STYLES.SUV.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.VAN.TYPE,
        codes: BODY_STYLES.VAN.CODES,
        value: BODY_STYLES.VAN.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.COMMERCIAL.TYPE,
        codes: BODY_STYLES.COMMERCIAL.CODES,
        value: BODY_STYLES.COMMERCIAL.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.PICKUP.TYPE,
        codes: BODY_STYLES.PICKUP.CODES,
        value: BODY_STYLES.PICKUP.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.PASSENGERVAN.TYPE,
        codes: BODY_STYLES.PASSENGERVAN.CODES,
        value: BODY_STYLES.PASSENGERVAN.TYPE,
    }),
    new BodyStyleFilterModel({
        type: BODY_STYLES.CREWVAN.TYPE,
        codes: BODY_STYLES.CREWVAN.CODES,
        value: BODY_STYLES.CREWVAN.TYPE,
    }),
]);

const CatalogRecord = Record({
    purchaseMethodOrder: PurchaseMethodsValues.LEASE | PurchaseMethodsValues.FINANCE,
    purchaseMethodOrderValues: CatalogFilterService.getPurchaseMethodSortDropDownValues(),

    paymentFrequency: PaymentFrequencies.MONTHLY,
    paymentFrequencyValues: PaymentFrequencyService.getPaymentFrequencyDropDownValues(LocaleService),

    bodyStylesFilterAll: true,
    bodyStylesFilterCodes: List([]),
    bodyStyles,

    preferredMake: FILTERS.MAKE_FILTER_ALL,
    makes: List(),

    modelSearchString: '',
});

class Catalog extends CatalogRecord {
    constructor(args) {
        const newArgs = {};

        if (args) {
            // customBodyStyle - If filters from XMS is available
            if (args.customBodyStyle) {
                newArgs.bodyStyles = args.customBodyStyle;
            }
            if (args.bodyStylesFilterCodes) {
                const codes = args.bodyStylesFilterCodes;

                if (args.customBodyStyle) {
                    newArgs.bodyStyles = args.customBodyStyle.map((bodyStyle) => {
                        return bodyStyle.set('selected', includes(codes, bodyStyle.codes[0]));
                    });
                } else {
                    newArgs.bodyStyles = bodyStyles.map((bodyStyle) => {
                        return bodyStyle.set('selected', includes(codes, bodyStyle.codes[0]));
                    });
                }
            }
        }

        super({ ...args, ...newArgs });
    }

    getMakeDropDownValues() {
        return this.makes
            .map((make) => {
                return {
                    value: make,
                    label: make === FILTERS.MAKE_FILTER_ALL ? LocaleService.t('FILTER.ALL_MAKES') : make,
                };
            })
            .toArray();
    }

    getMakeValue() {
        const makeDropDownValues = this.getMakeDropDownValues();
        const preferredMakeValue = find(makeDropDownValues, { value: this.preferredMake });
        return preferredMakeValue ? preferredMakeValue.value : FILTERS.MAKE_FILTER_ALL;
    }

    getPreferredMake() {
        const makeDropDownValues = this.getMakeDropDownValues();
        const preferredMakeValue = find(makeDropDownValues, { value: this.preferredMake });
        return preferredMakeValue;
    }

    getPurcheMethodOrderValue() {
        const purchaseMethodOrderValue = find(this.purchaseMethodOrderValues, { value: this.purchaseMethodOrder });
        return purchaseMethodOrderValue ? purchaseMethodOrderValue.value : '';
    }

    getPaymentFrequencyValue() {
        const paymentFrequencyValue = find(this.paymentFrequencyValues, {
            value: this.paymentFrequency || PaymentFrequencies.MONTHLY,
        });

        return paymentFrequencyValue ? paymentFrequencyValue.value : '';
    }

    getFilteredValues() {
        const filteredMake = this.preferredMake !== FILTERS.MAKE_FILTER_ALL ? this.preferredMake : '';
        const filteredBodyStyles = this.bodyStyles
            .filter((bodyStyle) => {
                return bodyStyle.selected;
            })
            .map((bodyStyle) => {
                return bodyStyle.label;
            });

        const filterMakeBody = filteredMake && filteredBodyStyles.size > 0 ? `${filteredMake}, ` : filteredMake;
        const filterMakeBodyJoin = (filteredMake || filteredBodyStyles.size > 0) && this.modelSearchString ? ', ' : '';
        const filterModelSearch = this.modelSearchString ? `"${toUpper(this.modelSearchString)}"` : '';

        return `${filterMakeBody}${join(filteredBodyStyles.toArray(), ', ')}${filterMakeBodyJoin}${filterModelSearch}`;
    }
}

export default Catalog;
